import { parse, extract, stringify, parseUrl } from 'query-string';
import { redirects } from '@v4/login/config';

export { parseUrl };

/**
 * Return a string in base64, encoding the characters that are not url safe.
 *
 * @param stringToEncode
 * @returns {string}
 * @throws {Error}
 */
export const urlSafeEncode = (stringToEncode) => encodeURIComponent(btoa(stringToEncode));

/**
 * Decode a base64 encoded string.
 *
 * @param stringToDecode
 * @returns {String}
 * @throws {Error}
 */
export const urlSafeDecode = (stringToDecode) => atob(decodeURIComponent(stringToDecode));

/**
 * Return an object containing the query string parameters.
 *
 * @returns {*}
 */
export const getQueryParams = () => parse(window.location.search);

/**
 * Given an encoded string, decode and return the next redirect uri if
 * it's allowed.
 *
 * @param next
 * @param nextDefaultURI
 * @returns {*}
 */
export const nextRedirectURI = (next, nextDefaultURI = redirects.REDIRECT_URI_AFTER_LOGIN) => {
    try {
        const decodedUrl = urlSafeDecode(next);
        const isAllowed = decodedUrl.startsWith(window.location.origin);

        if (!isAllowed) {
            return nextDefaultURI;
        }

        return decodedUrl;
    } catch (e) {
        return nextDefaultURI;
    }
};

/**
 * Stringify an object into a query string and appends the
 * question mark when needed.
 *
 * @param {object} params query params
 * @param {object} options query-string package options
 *
 * @returns {string} query string
 */
export const stringifyQueryParams = (params = {}, options = {}, encode = false) => {
    const queryParams = stringify(params, { encode, ...options });

    return `${queryParams ? '?' : ''}${queryParams}`;
};

/**
 * Gets query params from the browser and stringifies(?) them.
 *
 * @returns {string} query string
 */
export const getStringifiedQueryParamsFromBrowser = () => stringifyQueryParams(getQueryParams());

/**
 * Removes existing url parameters and updates window history
 *
 * @param {object} params query params
 * @param {object} options query-string package options
 * @return {void}
 */
export const windowHistoryReplaceStateParams = (params = {}, options = {}) => {
    const { url } = parseUrl(window.location.href);

    window.history.replaceState({}, document.title, `${url}${stringifyQueryParams(params, options, true)}`);
};

/**
 * Push new state and updates window history
 *
 * @param {object} params query params
 * @param {object} options query-string package options
 * @return {void}
 */
export const windowHistoryPushStateParams = (state = {}, params = {}, options = {}) => {
    const { url } = parseUrl(window.location.href);

    window.history.pushState(state, document.title, `${url}${stringifyQueryParams(params, options, true)}`);
};

/**
 * Updates window history url parameters by keeping the existing ones
 *
 * @param {object} params query params
 * @param {object} options query-string package options
 * @return {void}
 */
export const updateWindowHistoryReplaceState = (params = {}, options = {}) =>
    windowHistoryReplaceStateParams({ ...getQueryParams(), ...params }, options);

/**
 * Push new state into window history url parameters by keeping the existing ones
 *
 * @param {object} params query params
 * @param {object} options query-string package options
 * @return {void}
 */
export const updateWindowHistoryPushState = (state = {}, params = {}, options = {}) =>
    windowHistoryPushStateParams(state, { ...getQueryParams(), ...params }, options);

/**
 * Remove hash from URL
 *
 * @param {string} input URL
 * @return {string} URL without hash
 */
export const removeHash = (input) => {
    let url = input;
    const hashStart = url.indexOf('#');

    if (hashStart !== -1) {
        url = url.slice(0, hashStart);
    }

    return url;
};

/**
 * Get hash from URL
 *
 * @param {string} url URL
 * @return {string} Hashed value
 */
export const getHash = (url) => {
    let hash = '';
    const hashStart = url.indexOf('#');

    if (hashStart !== -1) {
        hash = url.slice(hashStart);
    }

    return hash;
};

/**
 * Stringify an object into a URL with a query string and sorting the keys. The inverse of `parseUrl()`
 *
 * @param {object} input Input
 * @param {string} input.url The URL to stringify.
 * @param {object} input.query Query items to add to the URL.
 * @param {object} options The options are the same as for `stringify()`
 * @return {string} String with the URL and a query string.
 */
export const stringifyUrl = (input, options) => {
    const url = removeHash(input.url).split('?')[0] || '';
    const queryFromUrl = extract(input.url);
    const parsedQueryFromUrl = parse(queryFromUrl);
    const hash = getHash(input.url);
    const query = { ...parsedQueryFromUrl, ...input.query };

    // create a query string and replace an ASCII symbol with normal comma to make all website urls coherent
    let queryString = stringify(query, options).replace('%2C', ',');

    if (queryString) {
        queryString = `?${queryString}`;
    }

    return `${url}${queryString}${hash}`;
};

export const untrailingSlashIt = (str) => str.replace(/\/$/, '');
export const trailingSlashIt = (str) => `${untrailingSlashIt(str)}/`;
